import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const CartContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  // Custom prices for the currently logged-in B2B buyer (productId -> custom_price)
  const [customPrices, setCustomPrices] = useState({});

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const refreshCustomPrices = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCustomPrices({});
      return;
    }
    try {
      const res = await axios.get(`${API}/my-prices`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomPrices(res.data || {});
    } catch (err) {
      setCustomPrices({});
    }
  }, []);

  // Fetch custom prices on mount and whenever auth token changes (login/logout)
  useEffect(() => {
    refreshCustomPrices();
    const onStorage = (e) => {
      if (e.key === 'token') refreshCustomPrices();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshCustomPrices]);

  const getEffectivePrice = useCallback((item) => {
    if (!item) return 0;
    const id = item.id || item.product_id;
    if (id && customPrices[id] !== undefined && customPrices[id] !== null) {
      return Number(customPrices[id]);
    }
    return Number(item.price || 0);
  }, [customPrices]);

  const addToCart = useCallback((product, quantity = 1) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setCartItems(prev => prev.filter(item => item.id !== productId));
      return;
    }
    setCartItems(prev => prev.map(item => item.id === productId ? { ...item, quantity } : item));
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + getEffectivePrice(item) * item.quantity, 0);
  }, [cartItems, getEffectivePrice]);

  const getCartCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  const value = useMemo(() => ({
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    customPrices,
    getEffectivePrice,
    refreshCustomPrices,
  }), [cartItems, addToCart, removeFromCart, updateQuantity, clearCart, getCartTotal, getCartCount, customPrices, getEffectivePrice, refreshCustomPrices]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
